import React, { useState } from 'react'
import { Badge, Button, Card, CardBody, Col, FormGroup, Input, InputGroup, InputGroupAddon, Label, Row, Table } from 'reactstrap'
import TitlePage from 'components/atoms/title-page'
import InboundJourneyApiV2 from 'api/v2/admins/inbound-journey-api-v2'
import { PHASE3_OUTBOUND_AUTH_STATES } from '../constants'
import { extractPhase3Deal, extractPhase3Deals, getPhase3CartItems, getPhase3ErrorMessage } from '../helpers/phase3-inbound-journey-helper'

const Phase3OutboundAuthenticationPage = ({ pageUtils, apiNamespace = 'outbound' }) => {
  const [ q, setQ ] = useState('')
  const [ deals, setDeals ] = useState([])
  const [ deal, setDeal ] = useState(null)
  const [ isLoading, setIsLoading ] = useState(false)
  const [ stateByItem, setStateByItem ] = useState({})
  const [ authenticatorByItem, setAuthenticatorByItem ] = useState({})
  const [ noteByItem, setNoteByItem ] = useState({})

  const handleSearch = async () => {
    setIsLoading(true)
    setDeal(null)
    try {
      const response = await InboundJourneyApiV2.searchDeals({ q })
      setDeals(extractPhase3Deals(response))
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    } finally {
      setIsLoading(false)
    }
  }

  const openDeal = async (dealId) => {
    setIsLoading(true)
    try {
      const response = await InboundJourneyApiV2.showDeal({ dealId })
      setDeal(extractPhase3Deal(response))
      setDeals([])
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    } finally {
      setIsLoading(false)
    }
  }

  const submitAuthentication = async (cartItem) => {
    const state = stateByItem[cartItem.id] || 'passed'

    try {
      await InboundJourneyApiV2.authenticateCartItem({
        dealId             : deal.id,
        cartItemId         : cartItem.id,
        state              : state,
        authenticator_name : authenticatorByItem[cartItem.id] || '',
        note               : noteByItem[cartItem.id] || '',
        apiNamespace       : apiNamespace
      })
      pageUtils?.setAlertMsg?.('Outbound authentication updated')
      openDeal(deal.id)
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    }
  }

  const cartItems = getPhase3CartItems(deal)

  return (
    <>
      <TitlePage mainTitle='Inbound Journey' subTitle='Outbound Authentication' />

      <Card className='mb-3'>
        <CardBody>
          <InputGroup>
            <Input value={ q } placeholder='Search deal / reference number / buyer / SKU' onChange={ (event) => setQ(event.target.value) } />
            <InputGroupAddon addonType='append'>
              <Button color='primary' disabled={ isLoading } onClick={ handleSearch }>
                Search Deal
              </Button>
            </InputGroupAddon>
          </InputGroup>
        </CardBody>
      </Card>

      { deals.length > 0 && (
        <Card className='mb-3'>
          <Table responsive hover size='sm' className='mb-0'>
            <thead>
              <tr>
                <th>ID</th>
                <th>Reference</th>
                <th>Buyer</th>
                <th>State</th>
                <th />
              </tr>
            </thead>
            <tbody>
              { deals.map((row) => (
                <tr key={ row.id }>
                  <td>{ row.id }</td>
                  <td>{ row.reference_number || row.payment_reference_number || '-' }</td>
                  <td>{ row.user_name || row.user?.detail?.name || row.user?.email || '-' }</td>
                  <td>{ row.state || '-' }</td>
                  <td className='text-right'><Button size='sm' color='primary' onClick={ () => openDeal(row.id) }>Open</Button></td>
                </tr>
              )) }
            </tbody>
          </Table>
        </Card>
      ) }

      { deal && (
        <Card>
          <CardBody>
            <Row className='mb-3'>
              <Col md='8'>
                <h3>Deal #{ deal.id }</h3>
                <div className='text-muted'>{ deal.reference_number || deal.payment_reference_number || '-' }</div>
              </Col>
              <Col md='4'>
                <div className='small text-muted'>Deal State</div>
                <Badge color='info'>{ deal.state || '-' }</Badge>
              </Col>
            </Row>

            <Table responsive size='sm'>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Current Auth</th>
                  <th>Submit State</th>
                  <th>Authenticator</th>
                  <th>Note</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                { cartItems.map((cartItem) => {
                  const product = cartItem.product || {}
                  const currentState = cartItem.outbound_authentication_state || (cartItem.authenticity_check_done ? 'passed' : 'pending')

                  return (
                    <tr key={ cartItem.id }>
                      <td style={{ minWidth: 260 }}>
                        <a className='font-weight-bold' href={ `/app/inbound-journey/products/${product.id}` }>{ product.sku || '-' }</a>
                        <div>{ product.name || '-' }</div>
                      </td>
                      <td>
                        <Badge color={ cartItem.authenticity_check_done ? 'success' : 'warning' }>{ currentState }</Badge>
                        <div className='small text-muted'>{ cartItem.authenticator || cartItem.outbound_authenticator_name || '-' }</div>
                      </td>
                      <td>
                        <Input
                          type='select'
                          value={ stateByItem[cartItem.id] || 'passed' }
                          onChange={ (event) => setStateByItem((prev) => ({ ...prev, [cartItem.id]: event.target.value })) }
                        >
                          { PHASE3_OUTBOUND_AUTH_STATES.map((state) => <option key={ state } value={ state }>{ state }</option>) }
                        </Input>
                      </td>
                      <td>
                        <Input
                          value={ authenticatorByItem[cartItem.id] || '' }
                          onChange={ (event) => setAuthenticatorByItem((prev) => ({ ...prev, [cartItem.id]: event.target.value })) }
                        />
                      </td>
                      <td>
                        <Input
                          type='textarea'
                          value={ noteByItem[cartItem.id] || '' }
                          onChange={ (event) => setNoteByItem((prev) => ({ ...prev, [cartItem.id]: event.target.value })) }
                        />
                      </td>
                      <td className='text-right'>
                        <Button color='success' size='sm' onClick={ () => submitAuthentication(cartItem) }>
                          Submit
                        </Button>
                      </td>
                    </tr>
                  )
                }) }
              </tbody>
            </Table>

            { cartItems.length === 0 && <div className='text-muted'>No cart items found for this deal.</div> }

            <FormGroup className='mt-3'>
              <Label className='text-muted small'>Reminder</Label>
              <div className='alert alert-info mb-0'>
                Shipment is gated by backend inbound journey patch. Every cart item should pass or be intentionally skipped before shipment update.
              </div>
            </FormGroup>
          </CardBody>
        </Card>
      ) }
    </>
  )
}

export default Phase3OutboundAuthenticationPage
